import { useState, useEffect } from "react";
import API from "../services/api";
import MainLayout from "../layouts/MainLayout";

export default function Claims() {
  const [form, setForm] = useState({});
  const [product, setProduct] = useState(null);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // 🔥 NEW (for admin table view)
  const [claims, setClaims] = useState([]);
  const [dealerFilter, setDealerFilter] = useState("");

  // ==========================
  // 🔐 GET TOKEN (SAFE FOR MOBILE + WEB)
  // ==========================
  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  // ==========================
  // 🔍 Fetch product by serial
  // ==========================
  const fetchProduct = async (serial) => {
    if (!serial) return;

    try {
      const res = await API.get("/product/" + serial, getHeaders());

      setProduct(res.data);

      setForm((prev) => ({
        ...prev,
        product_id: res.data.id
      }));

    } catch (err) {
      alert("❌ Product not found");
      setProduct(null);
    }
  };

  // ==========================
  // 📤 Submit claim (NO CHANGE IN LOGIC)
  // ==========================
  const submit = async () => {
    try {
      setLoading(true);

      if (!form.product_id) return alert("Scan product first");
      if (!form.customer_mobile) return alert("Enter mobile number");

      const fd = new FormData();

      // ❗ REMOVED HARD CODE (SAFE FIX)
      // fd.append("salesman_id", 2); ❌ backend already uses token

      fd.append("qr_value", form.product_id);
      fd.append("customer_name", form.customer_name || "");
      fd.append("customer_mobile", form.customer_mobile);
      fd.append("customer_cnic", form.customer_cnic || "");
      fd.append("address", form.address || "");
      fd.append("city", form.city || "");

      if (form.invoice) {
        fd.append("invoice", form.invoice);
      }

      await API.post("/claim", fd, getHeaders());

      alert("✅ OTP sent on WhatsApp");
      setStep(2);

    } catch (err) {
      alert(err.response?.data?.msg || "Error submitting claim");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // 🔐 VERIFY OTP (FIXED ENDPOINT)
  // ==========================
  const verify = async () => {
    try {
      setLoading(true);

      if (!otp) return alert("Enter OTP");

      // ❗ FIXED ENDPOINT (VERY IMPORTANT)
      await API.post(
        "/claim/verify-otp",
        {
          mobile: form.customer_mobile,
          otp,
          qr_value: form.qr_value, // optional if used
          customer_name: form.customer_name,
          address: form.address,
          city: form.city,
          customer_cnic: form.customer_cnic
        },
        getHeaders()
      );

      alert("🎉 Claim submitted successfully!");

      // 🔄 RESET
      setForm({});
      setProduct(null);
      setOtp("");
      setStep(1);

    } catch (err) {
      alert("❌ Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // 🔥 NEW: LOAD CLAIMS (ADMIN VIEW)
  // ==========================
  const loadClaims = async () => {
    try {
      const res = await API.get("/admin/claims", getHeaders());
      setClaims(res.data);
    } catch (err) {
      console.log("Claims load error");
    }
  };

  useEffect(() => {
    loadClaims();
  }, []);

  const filteredClaims = claims.filter(c =>
    !dealerFilter || c.dealer_name?.toLowerCase().includes(dealerFilter.toLowerCase())
  );

  return (
    <MainLayout>
      <h2>Sales Claim</h2>

      {/* ========================== */}
      {/* STEP 1: CLAIM FORM */}
      {/* ========================== */}
      {step === 1 && (
        <>
          <h3>📦 Scan Product</h3>

          <input
            placeholder="Enter Serial Number"
            onBlur={(e) => fetchProduct(e.target.value)}
          />

          {product && (
            <div className="card">
              <h4>{product.model}</h4>
              <p>Incentive: {product.incentive_amount}</p>
            </div>
          )}

          <h3>👤 Customer Details</h3>

          <input
            placeholder="Customer Name"
            value={form.customer_name || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, customer_name: e.target.value }))
            }
          />

          <input
            placeholder="Mobile"
            value={form.customer_mobile || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, customer_mobile: e.target.value }))
            }
          />

          <input
            placeholder="CNIC"
            value={form.customer_cnic || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, customer_cnic: e.target.value }))
            }
          />

          <input
            placeholder="Address"
            value={form.address || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, address: e.target.value }))
            }
          />

          <input
            placeholder="City"
            value={form.city || ""}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, city: e.target.value }))
            }
          />

          <input
            type="file"
            onChange={(e) =>
              setForm((prev) => ({ ...prev, invoice: e.target.files[0] }))
            }
          />

          <button onClick={submit} disabled={loading}>
            {loading ? "Submitting..." : "Submit Claim"}
          </button>
        </>
      )}

      {/* ========================== */}
      {/* STEP 2: OTP */}
      {/* ========================== */}
      {step === 2 && (
        <>
          <h3>🔐 OTP Verification</h3>

          <input
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />

          <button onClick={verify} disabled={loading}>
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </>
      )}

      {/* ========================== */}
      {/* 🔥 ADMIN TABLE (NEW) */}
      {/* ========================== */}
      <h2 style={{ marginTop: 40 }}>All Claims</h2>

      <input
        placeholder="Filter by Dealer"
        onChange={(e) => setDealerFilter(e.target.value)}
      />

      <table border="1" width="100%" style={{ marginTop: 20 }}>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Salesman</th>
            <th>Dealer</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {filteredClaims.map((c) => (
            <tr key={c.id}>
              <td>{c.customer_name}</td>
              <td>{c.salesman_name}</td> {/* 🔥 NEW */}
              <td>{c.dealer_name}</td>   {/* 🔥 NEW */}
              <td>{c.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </MainLayout>
  );
}